import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, List } from '@mui/material';

interface CustomScrollbarListboxProps
  extends React.HTMLAttributes<HTMLUListElement> {
  children?: React.ReactNode;
}

const SCROLLBAR_WIDTH = 12;
const MIN_THUMB_HEIGHT = 32;
const LISTBOX_CLASS = 'custom-scrollbar-listbox';

const CustomScrollbarListbox = React.forwardRef<
  HTMLUListElement,
  CustomScrollbarListboxProps
>(({ children, style, ...props }, ref) => {
  const listRef = useRef<HTMLUListElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [thumbHeight, setThumbHeight] = useState(MIN_THUMB_HEIGHT);
  const [thumbTop, setThumbTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartScroll, setDragStartScroll] = useState(0);

  useEffect(() => {
    if (typeof ref === 'function') {
      ref(listRef.current);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLUListElement | null>).current =
        listRef.current;
    }
  }, [ref]);

  const updateThumb = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const { scrollHeight, clientHeight, scrollTop } = list;
    if (scrollHeight <= clientHeight) {
      setThumbHeight(0);
      setThumbTop(0);
      return;
    }
    const ratio = clientHeight / scrollHeight;
    const height = Math.max(clientHeight * ratio, MIN_THUMB_HEIGHT);
    setThumbHeight(height);
    setThumbTop(
      (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - height),
    );
  }, []);

  useEffect(() => {
    updateThumb();
    const list = listRef.current;
    if (!list) return;
    list.addEventListener('scroll', updateThumb);
    window.addEventListener('resize', updateThumb);
    return () => {
      list.removeEventListener('scroll', updateThumb);
      window.removeEventListener('resize', updateThumb);
    };
  }, [updateThumb]);

  const onThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartY(e.clientY);
    setDragStartScroll(listRef.current?.scrollTop ?? 0);
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMouseMove = (e: MouseEvent) => {
      const list = listRef.current;
      if (!list) return;
      const { scrollHeight, clientHeight } = list;
      const deltaY = e.clientY - dragStartY;
      const scrollableHeight = scrollHeight - clientHeight;
      const thumbMovable = clientHeight - thumbHeight;
      list.scrollTop =
        dragStartScroll + (deltaY / thumbMovable) * scrollableHeight;
    };
    const onMouseUp = () => {
      setIsDragging(false);
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, dragStartY, dragStartScroll, thumbHeight, thumbTop]);

  const listboxStyle: React.CSSProperties = {
    ...style,
    position: 'relative',
    maxHeight: 240,
    overflowY: 'scroll',
    paddingRight: SCROLLBAR_WIDTH,
    scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // IE/Edge
  };

  return (
    <Box position="relative">
      <List
        {...props}
        ref={listRef}
        style={listboxStyle}
        className={LISTBOX_CLASS}
      >
        {children}
      </List>
      {thumbHeight > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 3,
            bottom: 3,
            right: 0,
            width: SCROLLBAR_WIDTH,
            height: '100%',
            background: 'transparent',
            transition: 'background 0.2s',
            borderRadius: 4,
            '&:hover': {
              transition: 'background 0.2s',
              background: 'rgba(0, 0, 0, 0.05)',
            },
          }}
        >
          <Box
            ref={thumbRef}
            sx={{
              position: 'absolute',
              top: thumbTop,
              right: 0,
              width: SCROLLBAR_WIDTH,
              height: thumbHeight - 4,
              background: isDragging ? '#a8a8a8' : '#c1c1c1',
              borderRadius: 4,
              cursor: 'pointer',
              transition: 'background 0.2s',

            }}
            onMouseDown={onThumbMouseDown}
          />
        </Box>
      )}

      {/*Hide native scrollbar in WebKit browsers*/}
      <style>{`
          .${LISTBOX_CLASS}::-webkit-scrollbar {
            display: none;
          }
        `}</style>
    </Box>
  );
});

CustomScrollbarListbox.displayName = 'CustomScrollbarListbox';

export default CustomScrollbarListbox;
